import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash, Upload } from '@phosphor-icons/react';
import {
  uploadServiceAccountJson,
  removeServiceAccountJson,
  vertexConfigStatus,
  vertexImage,
} from '@/local/vertexClient';
import { localStore } from '@/local/localStore';
import { clearAllBlobs } from '@/local/localBlobs';
import { reseedLocalDefaults, seedDemoEpisode } from '@/local/localBackend';
import { toast } from '@/state/toastStore';

interface VertexStatus {
  ready: boolean;
  project?: string;
}

export function AdminLocalPanel() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<VertexStatus>({ ready: false });
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const importInput = useRef<HTMLInputElement>(null);

  async function refresh() {
    setStatus(await vertexConfigStatus());
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onUpload(file: File) {
    setBusy(true);
    try {
      const text = await file.text();
      await uploadServiceAccountJson(text);
      toast.ok('Service account saved.');
      await refresh();
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onRemoveSa() {
    if (!confirm('Remove the service-account JSON from disk?')) return;
    try {
      await removeServiceAccountJson();
      toast.ok('Service account removed.');
      await refresh();
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Remove failed.');
    }
  }

  async function onTest() {
    setBusy(true);
    try {
      await vertexImage({ prompt: 'a single 32x32 pixel test image, abstract', aspectRatio: '1:1' });
      toast.ok('Vertex connection OK.');
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Test failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onSeed() {
    if (!confirm('Wipe local data and seed a demo episode?')) return;
    const uuid = await seedDemoEpisode();
    toast.ok('Demo episode seeded.');
    navigate(`/episode/${uuid}`);
  }

  async function onClear() {
    if (!confirm('Clear all local data? This cannot be undone.')) return;
    localStore.clearAll();
    await clearAllBlobs();
    toast.ok('Local data cleared.');
  }

  function onReseedDefaults() {
    if (!confirm('Replace style presets + global templates with the bundled defaults?')) return;
    reseedLocalDefaults();
    toast.ok('Defaults restored.');
  }

  function onExport() {
    const dump = localStore.exportAll();
    const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `studio-v2-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function onImportFile(file: File) {
    if (!confirm('Replace current local data with the contents of this file?')) return;
    try {
      const text = await file.text();
      const dump = JSON.parse(text) as Record<string, unknown>;
      localStore.importAll(dump);
      toast.ok('Local data imported. Reloading…');
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Import failed.');
    }
  }

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-[16px] font-semibold text-text-1">Vertex setup</h2>
        <p className="mt-1 text-[13px] text-text-3">
          Upload a Google Cloud service-account JSON to enable real Imagen3 generation in
          local mode. Without it, generation falls back to Canvas-rendered placeholders.
        </p>
        {status.ready && (
          <div className="mt-4 rounded-md border-l-2 border-err bg-err/10 p-3 text-[12px] text-text-1">
            Service-account key is on disk at{' '}
            <code className="font-mono text-text-2">.vertex-sa.local.json</code>. Do not
            commit this file. Do not share this machine. Click "Remove service account"
            when done.
          </div>
        )}

        <div className="mt-4 rounded-md bg-surface-2 p-4">
          <p className="font-mono text-[12px] text-text-3">
            Status:{' '}
            {status.ready ? (
              <span className="text-ok">
                ready · project <span className="text-text-1">{status.project}</span>
              </span>
            ) : (
              <span className="text-warn">no SA uploaded — falling back to mock</span>
            )}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onUpload(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-[12px] font-medium text-text-on-accent transition-colors hover:bg-accent-hover disabled:cursor-wait disabled:opacity-60"
            >
              <Upload size={14} weight="bold" />
              Upload SA JSON
            </button>
            {status.ready && (
              <>
                <button
                  type="button"
                  onClick={onTest}
                  disabled={busy}
                  className="rounded-md bg-surface-3 px-3 py-2 text-[12px] font-medium text-text-1 transition-colors hover:brightness-110 disabled:cursor-wait disabled:opacity-60"
                >
                  Test connection
                </button>
                <button
                  type="button"
                  onClick={onRemoveSa}
                  className="inline-flex items-center gap-2 rounded-md bg-surface-3 px-3 py-2 text-[12px] font-medium text-text-3 transition-colors hover:bg-err/20 hover:text-err"
                >
                  <Trash size={14} />
                  Remove service account
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-[16px] font-semibold text-text-1">Local data</h2>
        <p className="mt-1 text-[13px] text-text-3">
          Manage the localStorage + IndexedDB state for local mode.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <button
            type="button"
            onClick={onSeed}
            className="rounded-md bg-accent px-3 py-2 text-[12px] font-medium text-text-on-accent transition-colors hover:bg-accent-hover"
          >
            Seed demo episode
          </button>
          <button
            type="button"
            onClick={onReseedDefaults}
            className="rounded-md bg-surface-3 px-3 py-2 text-[12px] font-medium text-text-1 transition-colors hover:brightness-110"
          >
            Reseed defaults
          </button>
          <button
            type="button"
            onClick={onExport}
            className="rounded-md bg-surface-3 px-3 py-2 text-[12px] font-medium text-text-1 transition-colors hover:brightness-110"
          >
            Export local data
          </button>
          <input
            ref={importInput}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onImportFile(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => importInput.current?.click()}
            className="rounded-md bg-surface-3 px-3 py-2 text-[12px] font-medium text-text-1 transition-colors hover:brightness-110"
          >
            Import local data
          </button>
          <button
            type="button"
            onClick={onClear}
            className="rounded-md bg-surface-3 px-3 py-2 text-[12px] font-medium text-text-3 transition-colors hover:bg-err/20 hover:text-err"
          >
            Clear all local data
          </button>
        </div>
      </section>
    </div>
  );
}
